import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';
import NotFound from '@/pages/NotFound';
import { articles } from '@/mocks/articles';

function generateArticleSchema(article: (typeof articles)[0]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    url: `https://mjhl.com/articles/${article.slug}`,
    publisher: {
      '@type': 'Organization',
      name: 'MJHL',
    },
  };
}

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return <NotFound />;
  }

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateArticleSchema(article)),
        }}
      />
      <main className="min-h-screen bg-background-50">
        <article>
          <div className="pt-20">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '新闻动态', path: '/articles' },
                { label: article.title },
              ]}
            />

            <div className="w-full px-4 md:px-6 py-6 md:py-10">
              <div className="max-w-3xl mx-auto">
                <div className="flex items-center gap-3 text-xs text-foreground-500 mb-4">
                  <span className="px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 font-medium whitespace-nowrap">
                    {article.category}
                  </span>
                  <span>{article.date}</span>
                  <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                  <span>{article.readTime}</span>
                </div>

                <h1 className="text-xl md:text-3xl font-bold text-foreground-950 leading-tight">
                  {article.title}
                </h1>

                <div className="mt-4 flex items-center gap-3">
                  <span className="text-sm text-foreground-600">作者：{article.author}</span>
                </div>

                <div className="mt-6 md:mt-8 rounded-lg overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    title={`${article.title} - MJHL`}
                    className="w-full h-auto object-cover"
                  />
                </div>

                <div
                  className="mt-8 md:mt-10 prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />

                <div className="mt-8 pt-6 border-t border-background-200 flex flex-wrap gap-2">
                  {article.tags.map((tag, i) => (
                    <span
                      key={i}
                      className="px-3 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700 font-medium whitespace-nowrap"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                <div className="mt-10 pt-6 border-t border-background-200">
                  <Link
                    to="/articles"
                    className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-arrow-left-line"></i>
                    返回新闻列表
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
    </>
  );
}